<template>
  <div class="gameStepContainer">
    <div
      class="playerBlock"
      :class="[
        isMe(gameData?.ownerPlayer.userAddress) ? 'glow' : null,
        winnerLooserClass(gameData?.ownerPlayer.userAddress)
      ]"
    >
      <div class="userAvatarCircle" :class="isMe(gameData?.ownerPlayer.userAddress) ? 'glow' : null">
        <img :src="gameData?.ownerPlayer.userAvatarUrl" alt="user-avatar" />
      </div>
      <img class="coin" :src="gameData?.isOddBet ? '/img/icons/flipCoin.png' : '/img/icons/flipCoinChest.png'" />
      <p class="playerName">
        {{
          gameData?.ownerPlayer.userAddress?.includes(gameData?.ownerPlayer.userName)
            ? formatAddress(gameData?.ownerPlayer.userAddress)
            : gameData?.ownerPlayer.userName
        }}
      </p>
      <TavernFlipCoinBouncingArrow
        v-if="isMe(gameData?.ownerPlayer.userAddress) && !areAnimationsFinished"
        style="left: 5%"
      />
    </div>

    <div
      class="playerBlock"
      :class="[
        isMe(gameData?.joinerPlayer.userAddress) ? 'glow' : null,
        winnerLooserClass(gameData?.joinerPlayer.userAddress)
      ]"
    >
      <div class="userAvatarCircle" :class="isMe(gameData?.joinerPlayer.userAddress) ? 'glow' : null">
        <img :src="gameData?.joinerPlayer.userAvatarUrl" alt="user-avatar" />
      </div>
      <img class="coin" :src="gameData?.isOddBet ? '/img/icons/flipCoinChest.png' : '/img/icons/flipCoin.png'" />
      <p class="playerName">
        {{
          gameData?.joinerPlayer.userAddress?.toLowerCase().includes(gameData?.joinerPlayer.userName.toLowerCase())
            ? formatAddress(gameData?.joinerPlayer.userAddress)
            : gameData?.joinerPlayer.userName
        }}
      </p>
      <TavernFlipCoinBouncingArrow
        v-if="isMe(gameData?.joinerPlayer.userAddress) && !areAnimationsFinished"
        style="right: 5%"
      />
    </div>
  </div>
  <TavernFlipCoinWinLose
    v-if="areAnimationsFinished"
    :game-data="gameData"
    @go-to-stats="$emit('go-to-stats')"
    @get-out="$emit('get-out')"
  />
</template>

<script lang="ts" setup>
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import type { FlipCoinGame } from '~/types/FlipCoin';

const props = defineProps<{
  gameData: FlipCoinGame;
}>();

defineEmits(['go-to-stats', 'get-out']);
const { address } = useWeb3ModalAccount();
const areAnimationsFinished = inject<Ref<boolean>>('animationsFinished');

const isMe = (betAddress?: string): boolean => {
  return betAddress && address.value ? address.value.toLowerCase() === betAddress.toLowerCase() : false;
};

const isWinner = (compareAddress: string): boolean => {
  if (!props.gameData || !props.gameData.winner || !areAnimationsFinished) return false;

  const { winner } = props.gameData;

  return winner.toLowerCase() === compareAddress.toLowerCase();
};

const winnerLooserClass = (playerAddress?: string) => {
  if (!areAnimationsFinished?.value || !playerAddress) return '';

  return props.gameData?.winner && isWinner(playerAddress) ? 'winner' : 'looser';
};
</script>

<style lang="scss" scoped>
.gameStepContainer {
  position: absolute;
  bottom: 110px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  display: flex;
  justify-content: space-around;
  cursor: default;

  @media screen and (max-width: 479px) {
    gap: 5px;
    padding: 0 8px;
  }
}

.playerBlock {
  width: 296px;
  height: 50px;
  display: flex;
  justify-content: end;
  align-items: center;
  background-color: #070000;
  border: 2px solid #3e321a;
  border-radius: 12px;
  padding-right: 30px;
  position: relative;
  color: #a2a2a2;

  @media screen and (max-width: 479px) {
    &:first-child {
      justify-content: end;
      padding-right: 8px;
      .coin {
        left: 32px;
      }
      .userAvatarCircle {
        left: 0;
      }
    }

    &:last-child {
      justify-content: start;
      padding-right: 0;
      padding-left: 8px;

      .coin {
        right: 32px;
        left: unset;
      }
      .userAvatarCircle {
        right: 0;
        left: unset;
      }
    }
  }

  &.glow {
    & > p {
      color: #fff;
    }
  }

  &.winner {
    background: linear-gradient(to bottom, #00a906, #000701);
  }

  &.looser {
    background: linear-gradient(to bottom, #ff2024, #000701);
  }

  .playerName {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: 190px;
    font-size: 20px;
    font-weight: 600;

    @media screen and (max-width: 479px) {
      font-size: 12px;
    }
  }

  .coin {
    width: 43px;
    height: 43px;
    position: absolute;
    z-index: 2;
    left: 21px;

    @media screen and (max-width: 479px) {
      width: 38px;
      height: 38px;
      left: 16px;
    }
  }
}

.userAvatarCircle {
  position: absolute;
  left: -33px;
  width: 67px;
  height: 67px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #604020;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1a1a1a;
  @media screen and (max-width: 479px) {
    width: 52px;
    height: 52px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.glow {
  box-shadow:
    0 0 4px #ddcead,
    0 0 8px #ddcead80,
    inset 0 0 4px #ddcead33;
  border: 2px solid #b87d2b;
}
</style>
